import {
  PricebookBundle,
  IntervalTypeEnum,
  Price,
  PricebookBundleMapping,
} from '../../../models/PricebookData';
import { RevShareResult, RevShareEntry } from '../../../models/RevShareData';
import { AdminTierTypeEnum, tierOrder } from '../../../services/drillDown';
import { LicenseAgreementData } from '../../../api/apis/LicenseAgreementApi';

export type RevShareSettingOption = 'Set fee share' | 'No fee share';

export type RevShareMethod = '% of sale' | 'Fixed $ amt';

type RevShareStatus =
  | { type: 'not-available' }
  | { type: 'not-set' }
  | { type: 'disabled' }
  | {
      type: 'set';
      method: RevShareMethod;
      amount: number;
      baseAmount: number;
      percentage: number;
      fixedAmount: number;
      effectiveTimestamp?: string;
    };

export interface RevShareFormValues {
  pricebookId: number;
  parentTier?: AdminTierTypeEnum;
  parentRevShareFields?: RevShareFields;
  revShareSetting?: RevShareSettingOption;
  effectiveTimestamp?: string | null;
  expireTimestamp: string | null;
  setEndDate?: string; // 'Yes' | 'No'
  revShareMethod?: RevShareMethod;
  baseAmount?: number;
  revSharePct?: number;
  fixedAmount?: number;
  existingEntryId?: number;
}

export type RevShareFields = Pick<
  RevShareFormValues,
  'revShareMethod' | 'baseAmount' | 'revSharePct' | 'fixedAmount' | 'effectiveTimestamp'
> &
  Partial<Pick<RevShareFormValues, 'expireTimestamp' | 'setEndDate'>>;

export const getLicensedBasePricebookIds = (
  bundles: PricebookBundle[],
  licenseAgreements: LicenseAgreementData[],
): number[] => {
  const activeLicenseIds = new Set(
    licenseAgreements
      .filter(license => license.hasAccepted && license.docStatus)
      .map(license => license.pricebookId),
  );

  return bundles
    .filter(b => !b.pricebook.addOn && activeLicenseIds.has(b.pricebook.id))
    .map(b => b.pricebook.id);
};

export const filterOrgRevSharePricebooks = (
  bundles: PricebookBundle[],
  licenseAgreements: LicenseAgreementData[],
  bundleMappings: PricebookBundleMapping[],
): PricebookBundle[] => {
  const activeLicensePricebookIds = new Set(
    licenseAgreements
      .filter(license => license.hasAccepted && license.docStatus)
      .map(license => license.pricebookId),
  );

  const bundledAddOnIds = new Set(
    bundleMappings
      .filter(mapping => activeLicensePricebookIds.has(mapping.primaryPricebookId))
      .map(mapping => mapping.addOnPricebookId),
  );

  return bundles.filter(b => {
    if (!b.pricebook.addOn) {
      return activeLicensePricebookIds.has(b.pricebook.id);
    }
    return bundledAddOnIds.has(b.pricebook.id);
  });
};

export type PricebookInheritanceStatus = 'inherited' | 'custom' | 'not-set';

export const getPricebookInheritanceStatus = (
  revShareSettings: RevShareResult | undefined,
  pricebookId: number,
  adminTier: AdminTierTypeEnum,
): PricebookInheritanceStatus => {
  const entries = getEntriesForPricebook(revShareSettings, pricebookId, adminTier);
  if (entries.length === 0) return 'not-set';

  const hasCustomEntry = entries.some(
    entry => entry.tier && entry.tier.toLowerCase() === adminTier.toLowerCase(),
  );
  return hasCustomEntry ? 'custom' : 'inherited';
};

export const getEntriesForPricebook = (
  revShareSettings: RevShareResult | undefined,
  pricebookId: number,
  adminTier?: AdminTierTypeEnum,
): RevShareEntry[] => {
  if (!revShareSettings?.revenueShares) return [];

  const entries = revShareSettings.revenueShares
    .filter(entry => entry.pricebookId === pricebookId)
    .sort(
      (a, b) => new Date(b.effectiveTimestamp).getTime() - new Date(a.effectiveTimestamp).getTime(),
    );

  if (adminTier && entries.length > 0) {
    const adminTierIndex = tierOrder.indexOf(adminTier);
    let nearestTier: string | undefined;
    let nearestIndex = -1;

    for (const entry of entries) {
      if (!entry.tier) continue;
      const entryTier = entry.tier.toLowerCase();
      const entryIndex = tierOrder.findIndex(t => t.toLowerCase() === entryTier);
      if (entryIndex >= 0 && entryIndex <= adminTierIndex && entryIndex > nearestIndex) {
        nearestIndex = entryIndex;
        nearestTier = entryTier;
      }
    }

    if (nearestTier) {
      return entries.filter(entry => !entry.tier || entry.tier.toLowerCase() === nearestTier);
    }
  }

  return entries;
};

export const getRevShareMethod = (entry: RevShareEntry): RevShareMethod =>
  entry.revShareDorp === 'P' ? '% of sale' : 'Fixed $ amt';

export const calculateRevShareAmount = (
  method: RevShareMethod,
  baseAmount: number,
  revSharePct: number,
  fixedAmount: number,
): number => {
  return method === '% of sale' ? (baseAmount * revSharePct) / 100 : fixedAmount;
};

export const deriveRevShareStatus = (
  revShareSetting: RevShareEntry | null,
  price?: Price,
): RevShareStatus => {
  if (!revShareSetting) {
    if ((price !== undefined && price.price <= 0) || price?.intervalType === IntervalTypeEnum.None)
      return { type: 'not-available' };
    return { type: 'not-set' };
  }
  if (revShareSetting.disabledRevShare) return { type: 'disabled' };

  const method = getRevShareMethod(revShareSetting);
  const amount = calculateRevShareAmount(
    method,
    revShareSetting.baseAmount,
    revShareSetting.revSharePct,
    revShareSetting.fixedAmount,
  );

  return {
    type: 'set',
    method,
    amount,
    baseAmount: revShareSetting.baseAmount,
    percentage: revShareSetting.revSharePct,
    fixedAmount: revShareSetting.fixedAmount,
    effectiveTimestamp: revShareSetting.effectiveTimestamp,
  };
};

export const getRevShareAnnotation = (
  tierName: string,
  fields: RevShareFields | undefined,
): Partial<Record<keyof RevShareFields, string>> => {
  if (!fields) return {};

  const text: Partial<Record<keyof RevShareFields, string>> = {};

  if (fields.revShareMethod) {
    text.revShareMethod = `(${tierName} default is ${fields.revShareMethod.toLowerCase()})`;
  }

  if (fields.revShareMethod === '% of sale') {
    if (fields.baseAmount !== undefined && fields.baseAmount > 0) {
      text.baseAmount = `(<${tierName}> default is $${fields.baseAmount.toFixed(2)})`;
    }
    if (fields.revSharePct !== undefined && fields.revSharePct > 0) {
      text.revSharePct = `(<${tierName}> default is ${fields.revSharePct}%)`;
    }
  }

  if (
    fields.revShareMethod === 'Fixed $ amt' &&
    fields.fixedAmount !== undefined &&
    fields.fixedAmount > 0
  ) {
    text.fixedAmount = `(<${tierName}> default is $${fields.fixedAmount.toFixed(2)})`;
  }

  return text;
};

export const findNearestParentEntry = (
  revShareSettings: RevShareResult | undefined,
  pricebookId: number,
  adminTier: AdminTierTypeEnum,
): RevShareEntry | null => {
  if (!revShareSettings) return null;

  const entries = getEntriesForPricebook(revShareSettings, pricebookId);
  const adminTierIndex = tierOrder.indexOf(adminTier);

  let nearest: RevShareEntry | null = null;
  let nearestIndex = -1;

  for (const entry of entries) {
    if (!entry.tier) continue;
    const entryTier = entry.tier.toLowerCase();
    if (entryTier === adminTier.toLowerCase()) continue;
    const entryIndex = tierOrder.findIndex(t => t.toLowerCase() === entryTier);
    if (entryIndex >= 0 && entryIndex < adminTierIndex && entryIndex > nearestIndex) {
      nearestIndex = entryIndex;
      nearest = entry;
    }
  }

  return nearest;
};
